import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import Header from "./components/Header";
import Slider from "./components/Slider";
import Hero from "./components/Hero";
import Features from "./components/Features";
import FAQs from "./components/FAQs";
import Footer from "./components/Footer";
import Signup from "./components/Signup";
import ContactForm from "./components/ContactForm";
import ScrollToTop from "./components/ScrollToTop";
import TrustTestimonials from "./components/TrustTestimonials";
import ScrollToTopButton from "./components/ScrollToTopButton";
import SpecialOfferModal from "./components/SpecialOfferModal";
import BlogList from "./components/Blog/BlogList";
import BlogDetail from "./components/Blog/BlogDetail";
import ContactModal from "./components/Contact";
import InfographicModalSection from "./components/InfographicModalSection";
import Pricing from "./components/Pricing";
import { ChatBot } from "./components/ChatBot";
import { LanguageProvider } from "./context/LanguageContext";


// ✅ New pages
import PublicSector from "./pages/PublicSector/PublicSector";
import PublicSectorUseCases from "./pages/PublicSector/PublicSectorUseCases";
import PublicSectorCompliance from "./pages/PublicSector/PublicSectorCompliance";
import PublicSectorInstitutions from "./pages/PublicSector/PublicSectorInstitutions";
import AI from "./pages/AI/AI";
import Roadmap from "./pages/AI/Roadmap";
import Pilot from "./pages/AI/Pilot";
import OneRupeeEstimate from "./OneRupeeEstimate";

const DEFAULT_CHATBOT_API_URL = process.env.NODE_ENV === "production" ? window.location.origin : "http://localhost:8000";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const NormalizeLanding = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      const { pathname, search, hash } = window.location;
      if (!pathname || pathname === "") {
        window.history.replaceState({}, "", "/" + (search || "") + (hash || ""));
      }

      track("landing_env", {
        path: window.location.pathname || "",
        search: window.location.search || "",
        ref: document.referrer || "none",
        ua: navigator.userAgent || "unknown",
      });
    } catch (e) {
      // no-op
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    track("route_change", { path: location.pathname });
  }, [location.pathname]);

  return null;
};

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const openModal = () => {
    setIsContactModalOpen(true);
    track("contact_modal_open", { placement: "app" });
  };

  const closeModal = () => {
    setIsContactModalOpen(false);
    track("contact_modal_close", { placement: "app" });
  };

  return (
    <>
      <NormalizeLanding />

      <Header openModal={openModal} />
      <ScrollToTop />

      <Routes>
        <Route
          path="/"
          element={
            <>
              <SpecialOfferModal />

              <div className="pt-3">
                <Slider />
              </div>

              <div className="pt-10">
                <Hero openContactModal={openModal} />
              </div>

              <InfographicModalSection />
              <TrustTestimonials />
              <FAQs />

              <div className="flex justify-center my-6">
                <button
                  onClick={() => {
                    track("contact_demo_click", { placement: "home_cta" });
                    openModal();
                  }}
                  className="bg-orange-500 text-white py-2 px-6 rounded-full hover:bg-orange-600 transition font-semibold"
                >
                  Contact for Demo
                </button>
              </div>
            </>
          }
        />

        <Route path="/signup" element={<Signup />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<ContactForm />} />
        <Route path="/features" element={<Features />} />

        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />

        {/* ✅ REAL Public Sector page */}
        <Route
          path="/public-sector"
          element={<PublicSector openContactModal={openModal} />}
        />

        {/* ✅ REAL AI page */}
        <Route path="/ai" element={<AI openContactModal={openModal} />} />

        {/* Keep these placeholders for now (until you create pages) */}
        <Route path="/public-sector/use-cases" element={<PublicSectorUseCases />} />
        <Route path="/public-sector/compliance" element={<PublicSectorCompliance />} />
          <Route path="/public-sector/institutions" element={<PublicSectorInstitutions />} />
          <Route path="/ai/roadmap" element={<Roadmap openContactModal={openModal} />} />
          <Route path="/ai/pilot" element={<Pilot openContactModal={openModal} />} />

        <Route
          path="*"
          element={<h1 className="text-center text-2xl mt-10">Page Not Found</h1>}
        />
        <Route path="/1-rupee-estimate" element={<OneRupeeEstimate />} />
      </Routes>

      <Footer />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={closeModal}
        apiEndpoint="/submit"
      />

      <ScrollToTopButton />

      <ChatBot apiUrl={process.env.REACT_APP_API_URL || DEFAULT_CHATBOT_API_URL} onOpenContactModal={openModal} />
    </>
  );
}

export default App;